import { useCallback, useEffect, useRef, useState } from 'react'
import { useLanguage } from '../../contexts/LanguageContext'
import { useLead } from '../../contexts/LeadContext'
import { trackLead, sendConversionsApiEvent } from '../../tracking'
import { submitLeadToCRM, createEventId } from '../../utils/submitLeadToCRM'
import { isDuplicateLead, rememberLead } from '../../utils/leadStorage'
import type {
  CreditSituation,
  LeadPayload,
  PurchaseIntent,
  VehicleInterest,
} from '../../types/lead'

export type FormStatus = 'idle' | 'sending' | 'error' | 'success'

export interface FormValues {
  firstName: string
  phone: string
  email: string
  vehicleInterest: VehicleInterest
  purchaseIntent: PurchaseIntent
  creditSituation: CreditSituation
  consent: boolean
  /** Honeypot — must stay empty. */
  company: string
}

const INITIAL: FormValues = {
  firstName: '',
  phone: '',
  email: '',
  vehicleInterest: '',
  purchaseIntent: '',
  creditSituation: '',
  consent: false,
  company: '',
}

export function useLeadForm() {
  const { t, lang } = useLanguage()
  const {
    vehicleInterest,
    presetIntent,
    tradeIn,
    setLeadName,
    setVehicleInterest,
    setHasSubmitted,
  } = useLead()
  const [values, setValues] = useState<FormValues>(INITIAL)
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({})
  const [expanded, setExpanded] = useState(false)
  const [status, setStatus] = useState<FormStatus>('idle')
  const startedRef = useRef(false)
  const pendingRef = useRef<LeadPayload | null>(null)

  // Category chosen in the "What are you looking for" section pre-selects the field.
  useEffect(() => {
    if (!vehicleInterest) return
    setValues((prev) => ({ ...prev, vehicleInterest }))
    setExpanded(true)
  }, [vehicleInterest])

  // The trade-in section routes into this same flow with the intent preset.
  useEffect(() => {
    if (!presetIntent) return
    setValues((prev) => ({ ...prev, purchaseIntent: presetIntent }))
    setExpanded(true)
  }, [presetIntent])

  // FormStart tracking is intentionally dormant — see tracking.ts.
  const markStarted = useCallback(() => {
    if (startedRef.current) return
    startedRef.current = true
  }, [])

  const setField = useCallback(
    <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
      markStarted()
      setValues((prev) => ({ ...prev, [key]: value }))
      setErrors((prev) => ({ ...prev, [key]: undefined }))
      if (key === 'phone') setExpanded(true)
      if (key === 'vehicleInterest') setVehicleInterest(value as VehicleInterest)
    },
    [markStarted, setVehicleInterest],
  )

  const validate = useCallback((): boolean => {
    const next: Partial<Record<keyof FormValues, string>> = {}
    if (!values.firstName.trim()) next.firstName = t('form.err.firstName')
    if (values.phone.replace(/\D/g, '').length < 10) next.phone = t('form.err.phone')
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      next.email = t('form.err.email')
    if (!values.consent) next.consent = t('form.err.consent')
    setErrors(next)
    return Object.keys(next).length === 0
  }, [values, t])

  const send = useCallback(
    async (payload: LeadPayload) => {
      setStatus('sending')
      const result = await submitLeadToCRM(payload)
      if (!result.ok) {
        pendingRef.current = payload
        setStatus('error')
        return
      }
      // Duplicate Lead events for the same phone within 24h are suppressed — the
      // submission itself always goes through.
      if (!isDuplicateLead(payload.phone)) {
        trackLead({
          eventId: payload.eventId,
          vehicleInterest: payload.vehicleInterest,
          purchaseIntent: payload.purchaseIntent,
          language: payload.language,
        })
        void sendConversionsApiEvent(payload)
      }
      rememberLead({
        phone: payload.phone,
        name: payload.firstName,
        vehicle: payload.vehicleInterest,
      })
      setLeadName(payload.firstName)
      setHasSubmitted(true)
      pendingRef.current = null
      setStatus('success')
    },
    [setHasSubmitted, setLeadName],
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      // Honeypot: silently reject bots without revealing the rejection.
      if (values.company) return
      if (!validate()) return
      const now = new Date().toISOString()
      const payload: LeadPayload = {
        firstName: values.firstName.trim(),
        phone: values.phone.trim(),
        email: values.email.trim(),
        vehicleInterest: values.vehicleInterest,
        purchaseIntent: values.purchaseIntent,
        creditSituation: values.creditSituation,
        // Carried from the trade-in section when used. No value is displayed or estimated.
        ...(tradeIn ? { tradeIn } : {}),
        consent: true,
        consentTimestamp: now,
        // Verbatim consent string in the language the visitor actually saw.
        consentText: t('form.consent'),
        language: lang,
        source: 'miamicarqueen.com - Landing Page',
        eventId: createEventId(),
        timestamp: now,
      }
      await send(payload)
    },
    [values, validate, t, lang, send, tradeIn],
  )

  const retry = useCallback(async () => {
    if (pendingRef.current) await send(pendingRef.current)
  }, [send])

  return { values, errors, expanded, status, setField, handleSubmit, retry }
}
